import React, {useEffect, useState} from "react";
import {Button, CircularProgress, Grid, Link, Paper, TableCell, TableRow} from "@material-ui/core"
import PastInvestment from "../../../shared/PastInvestment";
import useFetchableData from "../../../../../hooks/useFetchableData";
import RectangularSkeleton from "../../../shared/Skeleton/RectangularSkeleton";
import SortableTable from "../../../shared/Tables/SortableTable";
import FormDialog from "../../../shared/Dialogs/FormDialog";
import {formatDateUS, humanizeMoney} from "../../../../../helpers/utils";
import CustomSnackbar from "../../../../user/shared/CustomSnackbar";
import useSnackbar from "../../../../../hooks/useSnackbar";
import useInvestorUpdater from "../../hooks/useInvestorUpdater";
import useInvestorDeleter from "../../hooks/useInvestorDeleter";
const MATransactionForm = React.lazy(() => import("../../../maValidations/form/MATransactionForm"));

const InvestmentHistorySection = (
    {
        isEditing, handleOnChange, handleOnChangeInField,
        handleOnError, session, investmentStyles, investmentStages,
        fetchInvestments, ...props
    }
) => {
    const [investmentHistoryErrors, setInvestmentHistoryErrors] = useState([]);
    const [selectedInvestmentHistory, setSelectedInvestmentHistory] = useState(null);
    const [investmentHistory, setInvestmentHistory] = useState([]);
    const [openMaTransactionModal, setOpenMaTransactionModal] = useState(false);
    const [selectedMaTransactionId, setSelectedMaTransactionId] = useState(null);
    const {data: investmentHistoryData, setData: setInvestmentHistoryData, initialFetch} = useFetchableData(fetchInvestments, "investment_history");
    const snackbar = useSnackbar();
    const updater = useInvestorUpdater(session, snackbar);
    const deleter = useInvestorDeleter(session, snackbar);

    useEffect(() => {
        handleOnChangeInField("tracked_investments_attributes", [...investmentHistory], initialFetch)
    }, [investmentHistory])

    useEffect(() => {
        setInvestmentHistory(investmentHistoryData)
        setInvestmentHistoryErrors(Array(investmentHistoryData.length).fill(false))
    }, [investmentHistoryData])

    const setInvestmentHistoryError = (index, error) => {
        let newErrors = [...investmentHistoryErrors];
        newErrors[index] = error;
        setInvestmentHistoryErrors(newErrors);
    }

    useEffect(() => {
        handleOnError(investmentHistoryErrors.some((error) => error))
    }, [investmentHistoryErrors]);

    const handleAddInvestmentHistory = () => {
        let newInvestmentHistory = [...investmentHistory];

        newInvestmentHistory.push({
            investment_date: undefined,
            total_raised: undefined,
            amount_invested: undefined,
            funded_company_id: undefined,
            investment_style_id: undefined,
            investment_stage_id: undefined,
            id: undefined,
            key: Date.now(),
        })

        setInvestmentHistory(newInvestmentHistory);
        setInvestmentHistoryErrors(prevState => ([...prevState, true]))
    }

    const handleRemoveInvestmentHistory = async (event, investment) => {
        event.stopPropagation();
        if (window.confirm(`Are you sure you want to delete this investment?`)) {
            await deleter.investments(investment);
            const newInvestmentHistory = investmentHistory.filter((i) => i.id !== investment.id);
            const deletedIndex = investmentHistory.findIndex((i) => i.id === investment.id);
            const newInvestmentHistoryErrors = investmentHistoryErrors.filter((error, index) => index !== deletedIndex);
            setInvestmentHistory(newInvestmentHistory);
            setInvestmentHistoryErrors(newInvestmentHistoryErrors)
            handleOnError(false);
            setSelectedInvestmentHistory(null);
        }
    }

    const tableInvestmentHistory = investmentHistory.filter((investment) => !investment._destroy && !investment.key);
    const removeError = (index) => setInvestmentHistoryErrors(investmentHistoryErrors.filter((error, i) => index !== i));

    const handleSaveInvestment = async (pastInvestment) =>{
        await updater.investments((pastInvestment))
        setSelectedInvestmentHistory(null);
    }

    const handleOpenMaTransaction = (event, maTransactionIdString) => {
        event.stopPropagation();
        // Extract numeric ID from "M/A123" format or use numeric ID directly
        let maId;
        if (typeof maTransactionIdString === 'string') {
            maId = parseInt(maTransactionIdString.replace('M/A', ''));
        } else {
            maId = parseInt(maTransactionIdString);
        }
        setSelectedMaTransactionId(maId);
        setOpenMaTransactionModal(true);
    };

    const handleCloseMaTransactionModal = () => {
        setOpenMaTransactionModal(false);
        setSelectedMaTransactionId(null);
    };

    return (
        <Grid container item xs={12}>
            <RectangularSkeleton loading={!initialFetch} width={1000} height={300}>
                <Paper className="form-container" elevation={3}>
                    <Grid item xs={12}>
                        <h3>Investment History</h3>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            {
                                tableInvestmentHistory.length > 0 && <SortableTable
                                    headCells={[
                                        { id: 'funded_company_name', numeric: false, disablePadding: false, label: 'Funded Company', sortable: true },
                                        { id: 'investment_date', numeric: false, disablePadding: false, label: 'Date', sortable: true },
                                        { id: 'amount_invested', numeric: false, disablePadding: false, label: 'Amount Invested', sortable: true },
                                        { id: 'total_raised', numeric: false, disablePadding: false, label: 'Total Raised', sortable: true },
                                        { id: 'ma_transaction_id', numeric: false, disablePadding: false, label: 'M&A Transaction ID', sortable: true },
                                        { id: 'options', numeric: false, disablePadding: false, label: null },
                                    ]}
                                    rows={tableInvestmentHistory}
                                    RowRenderer={
                                        (row, _isSelected, index, _handleCheck) => (<TableRow
                                            hover
                                            tabIndex={-1}
                                            key={row.id}
                                            style={{cursor: "pointer"}}
                                            onClick={(event) => setSelectedInvestmentHistory(row.id)}
                                        >
                                            <TableCell>
                                                {row.funded_company_name}
                                            </TableCell>
                                            <TableCell>
                                                {formatDateUS(row.investment_date)}
                                            </TableCell>
                                            <TableCell>
                                                {humanizeMoney(row.amount_invested)}
                                            </TableCell>
                                            <TableCell>
                                                {humanizeMoney(row.total_raised)}
                                            </TableCell>
                                            <TableCell>
                                                {row.ma_transaction_id ? (
                                                    <Link
                                                        onClick={(event) => handleOpenMaTransaction(event, row.ma_transaction_id)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        {typeof row.ma_transaction_id === 'string' && row.ma_transaction_id.startsWith('M/A')
                                                            ? row.ma_transaction_id
                                                            : `M/A${row.ma_transaction_id}`}
                                                    </Link>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Link onClick={(event) => handleRemoveInvestmentHistory(event, row)}>
                                                    Delete
                                                </Link>
                                            </TableCell>
                                        </TableRow>)
                                    }
                                    initialOrderBy={"funded_company_name"}
                                />
                            }
                        </Grid>
                        <Grid item xs={12}>
                            <Button color="primary" variant="contained" onClick={handleAddInvestmentHistory} style={{marginBottom: '20px'}}>Add Past Investment</Button>
                        </Grid>
                        <Grid item xs={12}>
                            {
                                investmentHistory.map((pastInvestment, index) => (<React.Fragment key={pastInvestment.id || pastInvestment.key}>
                                        { !!pastInvestment.key ? <PastInvestment
                                            key={pastInvestment.id || pastInvestment.key}
                                            session={session}
                                            pastInvestmentData={pastInvestment}
                                            index={index}
                                            investmentHistory={investmentHistory}
                                            setInvestmentHistory={setInvestmentHistory}
                                            removeInvestmentError={(i) => removeError(i)}
                                            investmentStyles={investmentStyles}
                                            investmentStages={investmentStages}
                                            handleOnError={(error) => setInvestmentHistoryError(index, error)}
                                            isEditing={isEditing}
                                            deleter={deleter}
                                            isNew
                                        /> : <FormDialog
                                            open={selectedInvestmentHistory === pastInvestment.id}
                                            label={"Investment History Form"}
                                            handleSave={() => handleSaveInvestment(pastInvestment)}
                                            handleClose={() => setSelectedInvestmentHistory(null)}
                                        >
                                            <PastInvestment
                                                key={pastInvestment.id || pastInvestment.key}
                                                session={session}
                                                pastInvestmentData={pastInvestment}
                                                index={index}
                                                investmentHistory={investmentHistory}
                                                setInvestmentHistory={setInvestmentHistory}
                                                removeInvestmentError={(i) => removeError(i)}
                                                investmentStyles={investmentStyles}
                                                investmentStages={investmentStages}
                                                handleOnError={(error) => setInvestmentHistoryError(index, error)}
                                                isEditing={isEditing}
                                                deleter={deleter}
                                                setSelectedInvestmentHistory={setSelectedInvestmentHistory}
                                            />
                                        </FormDialog>
                                        }

                                        <CustomSnackbar
                                            open={snackbar.open}
                                            closeHandler={snackbar.close}
                                            message={snackbar.message}
                                            options={{
                                                severity: 'success'
                                            }}
                                        />
                                    </React.Fragment>)
                                )
                            }
                        </Grid>
                    </Grid>
                </Paper>
            </RectangularSkeleton>

            {/* M&A Transaction Modal */}
            {openMaTransactionModal && selectedMaTransactionId && (
                <React.Suspense fallback={<CircularProgress />}>
                    <MATransactionForm
                        itemId={selectedMaTransactionId}
                        onClose={handleCloseMaTransactionModal}
                        open={openMaTransactionModal}
                        onDataUpdate={() => {}}
                        afterCreate={() => {}}
                        generalClasses={{}}
                        outsideSnackbar={snackbar}
                        setSelectedItemId={setSelectedMaTransactionId}
                        modalZIndex={1500}
                    />
                </React.Suspense>
            )}
        </Grid>
    )
}

export default InvestmentHistorySection;
